import React, { memo, useRef } from 'react';
import type { PreloadedQuery, EntryPointProps } from 'react-relay';
import { nav4SidebarGlobalProductsSettingsResource } from '@jira/router-resources__navigation-sidebar-global-products-settings/src/nav4SidebarGlobalProductsSettingsResource.tsx';
import { SidebarBoundary } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/ui/sidebar-boundary/index.tsx';
import { SidebarContent } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-settings-global-common/src/ui/sidebar-content/index.tsx';
import { Skeleton } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-settings-global-common/src/ui/skeleton/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import type { GlobalProductsSettingsSidebarQuery } from '@atlassian/jira-relay/src/__generated__/GlobalProductsSettingsSidebarQuery.graphql.ts';
import { useRelayResource } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import { SidebarReady } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/controllers/metrics/index.tsx';
import { GlobalProductsSettingsSidebar } from './GlobalProductsSettingsSidebar.tsx';

export function SidebarContentWithRelayResource(): React.JSX.Element {
	const { queryReference } = useRelayResource<GlobalProductsSettingsSidebarQuery>(
		nav4SidebarGlobalProductsSettingsResource,
	);
	const queryReferenceRef = useRef<PreloadedQuery<GlobalProductsSettingsSidebarQuery> | null>(null);

	// We don't want to use null query references. So only remember the non-null ones,
	// and pass the non-null query reference to the children.
	// From Slack: https://atlassian.slack.com/archives/C01E2MPPLE4/p1654736794416059?thread_ts=1654051102.548519&cid=C01E2MPPLE4
	if (queryReference) {
		queryReferenceRef.current = queryReference;
	}

	return <SidebarContentInner queryReference={queryReferenceRef.current} />;
}

type Queries = {
	nav4SidebarGlobalProductsSettings: GlobalProductsSettingsSidebarQuery;
};

export function SidebarContentForEntryPoint({
	queries: { nav4SidebarGlobalProductsSettings },
}: EntryPointProps<Queries, {}, {}, {}>): React.JSX.Element {
	return <SidebarContentInner queryReference={nav4SidebarGlobalProductsSettings} />;
}

type Props = {
	queryReference: PreloadedQuery<GlobalProductsSettingsSidebarQuery> | null;
};

export function SidebarContentInner({ queryReference }: Props): React.JSX.Element {
	return (
		<SidebarBoundary
			id="nav4.sidebars.settings.global.products.sidebar-content"
			packageName="navigationAppsSidebarNav4SidebarsSettingsGlobalProducts"
			ufoName="nav4.sidebars.settings.global.products.sidebar-content"
		>
			<SidebarContent
				currentPageName="products"
				testId="navigation-apps-sidebar-nav4-sidebars-settings-global-products.ui.sidebar-content"
			>
				<Placeholder
					name="nav4.sidebars.settings.global.products.sidebar-content"
					fallback={<Skeleton />}
				>
					{queryReference && <GlobalProductsSettingsSidebar queryReference={queryReference} />}
				</Placeholder>
			</SidebarContent>
			<SidebarReady />
		</SidebarBoundary>
	);
}

const LazySidebarContent = lazyForPaint(
	() =>
		import(
			/* webpackChunkName: "async-jira-navigation-apps-sidebar-nav4-sidebar-global-settings-products-settings" */ './main'
		),
	{
		includeInShellOnlySsrBundle: true,
		entryFsSsrLiftImportToModule: false,
	},
);

const AsyncSidebar = (): React.JSX.Element => {
	return (
		<SidebarBoundary
			id="nav4.sidebar.global-settings.products-settings"
			packageName="jiraNavigationAppsSidebarNav4GlobalSettingsProductsSettings"
			ufoName="nav4.sidebar.global-settings.products-settings"
		>
			<SidebarContent
				currentPageName="products"
				testId="navigation-apps-sidebar-nav4-sidebars-settings-global-products.ui.sidebar-content"
			>
				<Placeholder name="nav4.sidebar.global-settings.products-settings" fallback={<Skeleton />}>
					<LazySidebarContent />
				</Placeholder>
			</SidebarContent>
		</SidebarBoundary>
	);
};

export default memo(AsyncSidebar);
